/**
 * The activity log API.
 *
 * Every method takes the same flat `params` object, which is the filter state the page holds —
 * module, action, category, severity, outcome, actor, target, date range, free text and so on.
 * Passing the identical object to `list`, `stats` and `export` is what guarantees the table, the
 * summary strip and the CSV all describe the same set of rows.
 */
export default class ActivityLogsJwtService {
  axiosIns = null

  jwtConfig = null

  constructor(axiosIns, jwtConfig) {
    this.axiosIns = axiosIns;
    this.jwtConfig = jwtConfig;
  }

  get baseEndpoint() {
    return this.jwtConfig.main.activityLogsEndpoint;
  }

  /** Paginated, filtered list. */
  list(params) {
    return this.axiosIns.get(this.baseEndpoint, { params });
  }

  /** One entry in full, with its field-level diff and request context. */
  single(id) {
    return this.axiosIns.get(`${this.baseEndpoint}/${id}`);
  }

  /**
   * The full taxonomy plus which values actually occur in the current window, with counts.
   *
   * Fetched from the server rather than hard-coded here so the dropdowns cannot drift from the
   * values the API filters on — a module added on the server appears in the UI with no change
   * to this app.
   */
  filterOptions(params) {
    return this.axiosIns.get(`${this.baseEndpoint}/filter-options`, { params });
  }

  /** Totals, breakdowns and the per-day timeline, under the current filter. */
  stats(params) {
    return this.axiosIns.get(`${this.baseEndpoint}/stats`, { params });
  }

  /** Every entry from one logical operation, oldest first. */
  trace(correlationId) {
    return this.axiosIns.get(`${this.baseEndpoint}/trace/${correlationId}`);
  }

  /** The full history of one record — "who has ever touched this member profile". */
  recordHistory(collection, id, params) {
    return this.axiosIns.get(`${this.baseEndpoint}/history/${collection}/${id}`, { params });
  }

  /** CSV of the current filter. */
  export(params) {
    return this.axiosIns.get(`${this.baseEndpoint}/export`, {
      params,
      responseType: 'blob',
    });
  }
}
